import splitOnFirst from 'split-on-first';
import axios from 'axios';
import {
  hasAlternativelyUploaded,
  hasManufactureReadyArtwork,
  hasDocumentReferenceComplete,
} from '@printdeal/business-logic';
import { ServiceFactory } from '../services/ServiceFactory';
import LineItem from '../types/cart/LineItem';

type File = {
  name: string,
  preview: string | null,
  upload: string,
};

type Image = {
  fileName: string,
  originalSourceUrl: string,
  previewUrl: string,
};

const getMetaDataUrl = ({ image, url }: { image: Image, url: URL }) => {
  if (image.originalSourceUrl.includes('/original')) {
    return image.originalSourceUrl.replace('original', 'metadata');
  }

  if (image.originalSourceUrl.includes('/print')) {
    return image.originalSourceUrl.replace('print', 'metadata');
  }

  return `${url.origin}${url.pathname}/metadata${url.search}`;
};

const getUploadsByLineItem = async (lineItem: LineItem) => {
  const uploadUrls: string[] = [];
  const files: File[] = [];

  // Some legacy line items have no custom fields
  if (!lineItem.custom) {
    return { [lineItem.id]: { uploadUrls, files } };
  }

  const { custom: { fields } } = lineItem;
  if (hasDocumentReferenceComplete(lineItem) && fields.mcpDocumentsSource) {
    const docRefUrl = splitOnFirst(fields.mcpDocumentsSource[0], ':')[1];
    if (!docRefUrl) {
      return { [lineItem.id]: { uploadUrls, files } };
    }

    try {
      // If printQ editor is used original files are not stored in cimdoc,
      // that's why we are currently using a endpoint created at cloudlab side
      const getPrintQPages = async () => {
        const printQService = ServiceFactory.createPrintQService();
        const printQFiles = await printQService.getUploadedFiles(docRefUrl);
        if (!printQFiles) {
          return [{ images: [] }];
        }

        const printQImages = Object.values(printQFiles);
        return [{ images: printQImages }];
      };

      const { data: docRef } = await axios.get(docRefUrl);
      const { data: cimDoc } = await axios.get(docRef.cimDocUrl);
      const pages = fields.editorType === 'printqeditor'
        ? await getPrintQPages()
        : cimDoc.document.surfaces || cimDoc.document.panels;

      await Promise.all(pages.map(async ({ images }: { images: Image[] }) => // check surfaces
        images.length && await Promise.all(images.map(async (image) => {
          if (!image?.originalSourceUrl) {
            return;
          }

          if (!uploadUrls.includes(image.originalSourceUrl)) {
            uploadUrls.push(image.originalSourceUrl);
          }
          if (!files.some((file) => file.preview === image.previewUrl)) {
            let fileName = 'Download';
            const url = new URL(image.originalSourceUrl);
            // Get the metaDataUrl for all the editors apart from printQ
            if (fields.editorType !== 'printqeditor') {
              const { data: { uploadedFileName } } = await axios.get(getMetaDataUrl({ image, url }));
              fileName = uploadedFileName;
            }
            files.push({
              preview: image.previewUrl,
              upload: image.originalSourceUrl,
              name: fileName || image.fileName,
            });
          }
        }))).flat());
    } catch (error) {
      // Do nothing
    }
  }
  if (hasAlternativelyUploaded(lineItem) && fields.userUploadsUrl) {
    const [, fileName, fileUrl] = fields.userUploadsUrl[0].split('::');
    uploadUrls.push(fileUrl);
    files.push({
      preview: null,
      upload: fileUrl,
      name: fileName,
    });
  }
  if (hasManufactureReadyArtwork(lineItem)) {
    uploadUrls.push(fields.manufacturingReadyDocumentUrl);
    const { fileName } = JSON.parse(fields.mrdMetadata) as { fileName: string };
    files.push({
      preview: null,
      upload: fields.manufacturingReadyDocumentUrl,
      name: fileName,
    });
  }
  return { [lineItem.id]: { uploadUrls, files } };
};

const getFileInfo = async (lineItems: LineItem[]) => {
  const getFileRequests = lineItems.map((lineItem) => getUploadsByLineItem(lineItem));
  const getFileResponses = await Promise.all(getFileRequests);
  const uploadsByLineItem = Object.assign(
    {},
    ...getFileResponses,
  ) as { [ lineItemId: string]: { uploadUrls: string[], files: File[] } };
  const validByLineItem = Object.entries(uploadsByLineItem).map(async ([lineItemId, { uploadUrls, files }]) => {
    try {
      const requests = uploadUrls.map((fileUrl) => axios.head(fileUrl));
      await Promise.all(requests);
      // eslint-disable-next-line @typescript-eslint/return-await
      return Promise.resolve({ [lineItemId]: { valid: true, files } });
    } catch (error) {
      return Promise.resolve({ [lineItemId]: { valid: false, files } });
    }
  });

  const valid = await Promise.all(validByLineItem);
  return Object.assign({}, ...valid);
};

export { getFileInfo };
